<template>
  <el-dialog :title="$t('设置押金阈值')" :visible="visible" width="640px" top="6vh" custom-class="deposit-target-dialog" append-to-body :close-on-click-modal="false" :close-on-press-escape="!saving" :show-close="!saving" @update:visible="close" @open="load">
    <div slot="title" class="target-heading">
      <span class="target-heading-icon"><i class="el-icon-wallet" aria-hidden="true" /></span>
      <div><h2>{{ $t('设置押金阈值') }}</h2><p>{{ $t('设置客户应维持的押金金额') }}</p></div>
    </div>
    <div v-loading="loading" class="target-content">
      <div class="target-customer">
        <span class="target-avatar"><i class="el-icon-user" aria-hidden="true" /></span>
        <div class="target-customer-name"><span>{{ $t('客户名称') }}</span><strong>{{ user.nickname || user.username || '-' }}</strong><small v-if="user.nickname && user.username">{{ user.username }}</small></div>
        <span class="target-currency">USD</span>
      </div>
      <div class="target-metrics">
        <div class="target-metric target-metric--primary"><span>{{ $t('当前阈值') }}</span><strong>{{ money(account.depositTarget) }}</strong></div>
        <div class="target-metric"><span>{{ $t('当前押金') }}</span><strong>{{ money(account.deposit) }}</strong></div>
        <div class="target-metric"><span>{{ $t('可用余额') }}</span><strong>{{ money(account.availableBalance) }}</strong></div>
      </div>
      <el-form label-position="top" class="target-form">
        <el-form-item :label="$t('新的押金阈值')">
          <div class="target-amount-control"><el-input-number v-model="amount" :disabled="loading || saving" :min="0" :max="99999999.99" :precision="2" :step="100" controls-position="right" :aria-label="$t('新的押金阈值')" /><span>USD</span></div>
        </el-form-item>
      </el-form>
      <section class="target-preview">
        <div class="target-preview-heading"><h3>{{ $t('调整预览') }}</h3><span class="target-status" :class="{ 'target-status--due': plan.shortfall > 0 }"><i :class="plan.shortfall > 0 ? 'el-icon-warning-outline' : 'el-icon-circle-check'" aria-hidden="true" />{{ plan.shortfall > 0 ? $t('待补押金') : $t('无需补足') }}</span></div>
        <div class="target-preview-row"><span><i class="el-icon-bottom-left" aria-hidden="true" />{{ $t('预计从余额补入押金') }}</span><strong :class="{ 'target-transfer-in': plan.transferIn > 0 }">{{ money(plan.transferIn) }} <small>USD</small></strong></div>
        <div class="target-preview-row"><span><i class="el-icon-top-right" aria-hidden="true" />{{ $t('预计退回余额') }}</span><strong :class="{ 'target-transfer-out': plan.transferOut > 0 }">{{ money(plan.transferOut) }} <small>USD</small></strong></div>
        <div class="target-preview-row target-preview-row--total"><span>{{ $t('调整后待补押金') }}</span><strong :class="{ shortage: plan.shortfall > 0 }">{{ money(plan.shortfall) }} <small>USD</small></strong></div>
      </section>
      <div class="target-help"><i class="el-icon-info" aria-hidden="true" /><div><p>{{ $t('上调阈值：余额足够时补足差额，余额不足时显示待补押金。') }}</p><p>{{ $t('下调阈值：超出新阈值的押金退回余额。') }}</p><p>{{ $t('实际金额以保存时账户状态为准。') }}</p></div></div>
    </div>
    <div slot="footer" class="target-footer"><span class="target-record-note"><i class="el-icon-document-checked" aria-hidden="true" />{{ $t('所有调整均保留流水记录') }}</span><div class="target-footer-actions"><el-button :disabled="saving" @click="close(false)">{{ $t('取消') }}</el-button><el-button type="primary" icon="el-icon-check" :loading="saving" :disabled="loading || !loaded" @click="save">{{ $t('确认调整') }}</el-button></div></div>
  </el-dialog>
</template>

<script>
import { getAccount } from '../../api/transport'
import { updateDepositTarget } from '../../api/user'
import { translateUi } from '../../i18n'
import { depositTargetPlan } from './depositTarget'

export default {
  name: 'DepositTargetDialog',
  props: { visible: Boolean, user: { type: Object, default: () => ({}) } },
  data: () => ({ loading: false, loaded: false, saving: false, amount: 0, account: {}, loadRevision: 0 }),
  computed: {
    userId () { return this.user.userId || this.user.id },
    plan () { return depositTargetPlan(this.account, this.amount) }
  },
  methods: {
    money (value) { return Number(value || 0).toFixed(2) },
    close (value) { if (!this.saving) this.$emit('update:visible', value) },
    async load () {
      const revision = ++this.loadRevision
      this.loaded = false
      this.loading = true
      this.account = {}
      try {
        const account = await getAccount(this.userId)
        if (revision !== this.loadRevision) return
        this.account = account
        this.amount = Number(account.depositTarget || 0)
        this.loaded = true
      } catch (error) { /* request handler displays the API error */ } finally { if (revision === this.loadRevision) this.loading = false }
    },
    async save () {
      if (this.saving || !this.loaded || !this.userId) return
      if (!Number.isFinite(this.amount) || this.amount < 0 || this.amount > 99999999.99) return this.$message.warning(translateUi('押金阈值必须在 0 至 99999999.99 之间'))
      this.saving = true
      try {
        await updateDepositTarget(this.userId, this.amount)
        this.$message.success(translateUi('押金阈值已保存'))
        this.$emit('update:visible', false)
        this.$emit('saved')
      } catch (error) { /* request handler displays the API error */ } finally { this.saving = false }
    }
  }
}
</script>

<style lang="scss">
.deposit-target-dialog {
  display: flex; flex-direction: column; max-width: calc(100% - 32px); max-height: 88vh; margin-bottom: 24px; border-radius: 16px; overflow: hidden; color: #283c53; box-shadow: 0 24px 80px rgba(22, 42, 69, .2);
  .el-dialog__header { flex-shrink: 0; padding: 24px 28px 20px; border-bottom: 1px solid #edf0f5; }
  .el-dialog__headerbtn { top: 28px; right: 24px; font-size: 19px; }
  .el-dialog__body { min-height: 0; overflow-y: auto; padding: 24px 28px; word-break: normal; overflow-wrap: break-word; }
  .el-dialog__footer { flex-shrink: 0; padding: 18px 28px; background: #f8fafc; border-top: 1px solid #edf0f5; }
  .target-heading { display: flex; align-items: center; gap: 14px; padding-right: 24px; }
  .target-heading-icon { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 44px; height: 44px; border-radius: 12px; color: #3976c4; background: #edf4ff; font-size: 23px; }
  .target-heading h2 { margin: 0 0 6px; font-size: 19px; line-height: 1.4; font-weight: 600; color: #263e59; }
  .target-heading p { margin: 0; font-size: 12px; line-height: 1.5; color: #7c8b9f; }
  .target-customer { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
  .target-avatar { display: flex; align-items: center; justify-content: center; width: 38px; height: 38px; flex-shrink: 0; border: 1px solid #e8edf4; border-radius: 50%; color: #7990ab; background: #f7f9fc; font-size: 20px; }
  .target-customer-name { flex: 1; min-width: 0; }
  .target-customer-name > span { display: block; margin-bottom: 4px; font-size: 11px; color: #7f8da0; }
  .target-customer-name strong { font-size: 15px; font-weight: 600; color: #30475f; overflow-wrap: anywhere; }
  .target-customer-name small { margin-left: 8px; font-size: 12px; color: #8996a7; overflow-wrap: anywhere; }
  .target-currency { padding: 5px 9px; border: 1px solid #e3eaf4; border-radius: 5px; font-size: 11px; font-weight: 600; letter-spacing: 1px; color: #647b96; }
  .target-metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 24px; }
  .target-metric { padding: 16px 14px; border: 1px solid #e6ecf3; border-radius: 10px; background: #fff; }
  .target-metric > span { display: block; min-height: 18px; margin-bottom: 10px; font-size: 12px; line-height: 1.5; color: #7b8b9f; overflow-wrap: normal; word-break: normal; }
  .target-metric strong { display: block; font-size: 21px; line-height: 1.4; letter-spacing: -.4px; color: #344c67; font-weight: 600; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  .target-metric--primary { background: #f1f6fe; border-color: #dde9fa; }
  .target-metric--primary > span, .target-metric--primary strong { color: #386eb5; }
  .target-form { margin-bottom: 22px; }
  .target-form .el-form-item { margin: 0; }
  .target-form .el-form-item__label { padding-bottom: 10px; line-height: 1.5; font-size: 13px; font-weight: 600; color: #344c67; }
  .target-amount-control { display: flex; align-items: center; gap: 12px; }
  .target-amount-control .el-input-number { flex: 1; width: 100%; min-width: 0; line-height: 52px; }
  .target-amount-control .el-input__inner { height: 54px; padding-left: 18px; border-radius: 9px; text-align: left; font-size: 24px; font-weight: 600; font-variant-numeric: tabular-nums; color: #2d4a6b; border-color: #cedbec; }
  .target-amount-control .el-input__inner:focus { border-color: #5b91d5; }
  .target-amount-control .el-input-number__increase, .target-amount-control .el-input-number__decrease { right: 0; z-index: 1; display: flex; align-items: center; justify-content: center; box-sizing: border-box; width: 46px; height: 50%; margin: 0; background: #f5f8fc; color: #6e87a7; border-left-color: #e2e9f3; line-height: 1; }
  .target-amount-control .el-input-number__increase { top: 0; border-bottom: 1px solid #e2e9f3; }
  .target-amount-control .el-input-number__decrease { bottom: 0; border-top: 0; }
  .target-amount-control > span { font-size: 13px; font-weight: 600; color: #7c8ea3; }
  .target-preview { border: 1px solid #e3eaf4; border-radius: 11px; padding: 16px 18px 0; background: #fff; }
  .target-preview-heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 14px; }
  .target-preview-heading h3 { margin: 0; color: #344c67; font-size: 13px; font-weight: 600; }
  .target-status { display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0; padding: 4px 8px; color: #3f9276; background: #edf8f3; border-radius: 5px; line-height: 1.5; font-size: 11px; }
  .target-status--due { color: #c06735; background: #fff4e8; }
  .target-preview-row { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; padding: 9px 0; font-size: 12px; line-height: 1.6; color: #7b8b9f; }
  .target-preview-row > span { min-width: 0; }
  .target-preview-row > span i { margin-right: 8px; color: #93a5bb; }
  .target-preview-row strong { flex-shrink: 0; font-size: 15px; font-weight: 600; font-variant-numeric: tabular-nums; color: #344c67; }
  .target-preview-row small { margin-left: 3px; font-size: 10px; font-weight: 400; color: #8d9aae; }
  .target-preview-row--total { margin-top: 10px; padding: 14px 0; border-top: 1px dashed #e0e7f0; color: #405b78; }
  .target-preview-row--total strong { font-size: 19px; }
  .target-preview .target-transfer-in { color: #3976c4; }
  .target-preview .target-transfer-out { color: #339374; }
  .target-preview .shortage { color: #d35b51; }
  .target-help { display: flex; align-items: flex-start; gap: 8px; margin-top: 16px; color: #8794a6; font-size: 11px; line-height: 1.85; }
  .target-help > i { margin-top: 4px; color: #91a5bd; font-size: 13px; }
  .target-help p { margin: 0; }
  .target-footer { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
  .target-record-note { display: flex; align-items: center; gap: 6px; font-size: 11px; line-height: 1.6; color: #8c9aad; text-align: left; }
  .target-record-note i { font-size: 15px; }
  .target-footer-actions { display: flex; flex-shrink: 0; gap: 10px; }
  .target-footer-actions .el-button { min-width: 82px; padding: 11px 18px; border-radius: 7px; }
  .target-footer-actions .el-button + .el-button { margin-left: 0; }
  .target-footer-actions .el-button--primary { background: #3976c4; border-color: #3976c4; }
  .target-footer-actions .el-button--primary:hover { background: #4c87d2; border-color: #4c87d2; }
  .target-footer-actions .el-button--primary.is-disabled { background: #a6c3e7; border-color: #a6c3e7; }
}
@media (max-width: 600px) {
  .deposit-target-dialog {
    max-width: calc(100% - 24px);
    .el-dialog__header { padding: 20px 18px 18px; }
    .el-dialog__headerbtn { top: 24px; right: 18px; }
    .el-dialog__body { padding: 20px 18px; }
    .el-dialog__footer { padding: 16px 18px; }
    .target-heading { gap: 10px; }
    .target-heading h2 { font-size: 17px; }
    .target-heading-icon { width: 38px; height: 38px; font-size: 21px; }
    .target-metrics { gap: 8px; }
    .target-metric { padding: 12px 10px; }
    .target-metric > span { font-size: 11px; }
    .target-metric strong { font-size: 17px; }
    .target-customer-name small { display: block; margin: 4px 0 0; }
    .target-preview { padding: 14px 14px 0; }
    .target-preview-row { gap: 10px; }
    .target-footer { flex-direction: column; align-items: stretch; gap: 10px; }
    .target-footer-actions .el-button { flex: 1; min-width: 70px; padding: 10px 12px; }
    .target-footer-actions .el-button--primary { flex: 1.4; }
    .target-record-note { justify-content: center; font-size: 10px; }
  }
}
</style>
